import { useCallback, useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'

import Button from '@/elements/Button'
import Form from '@/elements/Form'
// import FormFile from '@/elements/FormFile'
import FormInput from '@/elements/FormInput'
import OtpInput from '@/elements/OtpInput'
import Flex from '@/elements/Flex'
import FormSelect from '@/elements/FormSelect'
import Text from '@/elements/Text'
import FormToggle from '@/elements/FormToggle'
import Modal from '@/components/Modal'
import useAuthUserForm from '@/hooks/forms/useAuthUserForm'
import toast from '@/utils/toast'
import useAuthStore from '@/stores/authStore'
import { requestEmailChange, confirmEmailChange } from '@/api/user'
import useBreakpoints from '@/hooks/useBreakpoints'
import { SystemUserRoles } from '@/api/types/Organization'
import useRoles from '@/hooks/useRoles'
import { getUserLocation } from '@/utils/location'
import { useTheme } from '@/utils/theme'

import AuthenticationDetails from './AuthenticationDetails'

function SettingsBasicInfo() {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [emailChangeStep, setEmailChangeStep] = useState<
    'request' | 'confirm' | null
  >(null)
  const [pendingEmail, setPendingEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [isEmailChangeLoading, setIsEmailChangeLoading] = useState(false)
  const { theme, toggle } = useTheme()
  const { role, roleName } = useRoles()
  const { user, deleteUser } = useAuthStore()
  const { isTablet, isMobile } = useBreakpoints()
  const { form } = useAuthUserForm({
    name: user?.details.firstName || '',
    surname: user?.details.lastName || '',
    email: user?.email || '',
    location: user?.details
      ? getUserLocation(user.details)
      : 'Unknown location',
    company: 'Fuzzy',
    role,
    avatar: user?.assets.PROFILE_PICTURE || '',
  })

  const rolesAsOptions = useMemo(() => {
    return SystemUserRoles.map(role => ({
      value: role,
      text: roleName || '',
    }))
  }, [roleName])

  const submitUpdateAuthUser = useCallback(async () => {
    const isValid = await form.trigger()

    if (isValid) {
      const data = {
        ...form.getValues(),
      }

      // TODO: Implement user update logic
      toast.success('Your profile has been updated successfully!')

      form.reset(data)
    } else {
      toast.error('Please fix the errors in the form.')
    }
  }, [form])

  const closeEmailChangeModal = useCallback(() => {
    setEmailChangeStep(null)
    setPendingEmail('')
    setOtpCode('')
  }, [])

  const handleRequestEmailChange = useCallback(async () => {
    if (!pendingEmail) return
    setIsEmailChangeLoading(true)
    try {
      await requestEmailChange({ newEmail: pendingEmail })
      setEmailChangeStep('confirm')
    } catch (error) {
      const message = (error as { message?: string })?.message
      toast.error(message || 'Failed to send verification code')
    } finally {
      setIsEmailChangeLoading(false)
    }
  }, [pendingEmail])

  const handleConfirmEmailChange = useCallback(async () => {
    if (!pendingEmail || !otpCode) return
    setIsEmailChangeLoading(true)
    try {
      await confirmEmailChange({ newEmail: pendingEmail, code: otpCode })
      useAuthStore.setState(state => ({
        user: state.user ? { ...state.user, email: pendingEmail } : state.user,
      }))
      form.setValue('email', pendingEmail)
      toast.success('Email updated successfully!')
      closeEmailChangeModal()
    } catch (error) {
      const message = (error as { message?: string })?.message
      toast.error(message || 'Invalid or expired verification code')
    } finally {
      setIsEmailChangeLoading(false)
    }
  }, [pendingEmail, otpCode, closeEmailChangeModal, form])

  const handleDeleteAccount = useCallback(async () => {
    try {
      await deleteUser()
    } catch {
      toast.error(
        'An error occurred while deleting your account. Please try again later.',
      )
    } finally {
      setShowDeleteModal(false)
    }
  }, [deleteUser])

  return (
    <Flex wfull className='card' gap={8} p={isMobile ? 3 : isTablet ? 5 : 7}>
      <Flex wfull inline={!isTablet} gap={4} wrap>
        <Flex fraction={4} gap={2} wfull>
          <Text variant='h3'>Basic Information</Text>
          <Text variant='h4' color='black-light-40'>
            Update your personal information and profile details.
          </Text>
        </Flex>
        <Flex fraction={6} wfull>
          <Form onSubmit={submitUpdateAuthUser}>
            {/* <Controller
            name='avatar'
            control={form.control}
            render={({ field }) => (
              <FormFile
                {...field}
                files={[]}
                label='Profile Picture'
                name='profilePicture'
                error={form.formState.errors.avatar?.message}
              />
            )}
          /> */}
            <Flex className='form-group'>
              <Controller
                name='name'
                control={form.control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    disabled
                    label='Name'
                    placeholder='Enter your name'
                    dirty={form.formState.dirtyFields.name}
                    error={form.formState.errors.name?.message}
                  />
                )}
              />
              <Controller
                name='surname'
                control={form.control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    disabled
                    label='Surname'
                    placeholder='Enter your surname'
                    dirty={form.formState.dirtyFields.surname}
                    error={form.formState.errors.surname?.message}
                  />
                )}
              />
            </Flex>
            <Flex
              inline
              wrap
              gap={3}
              align='flex-end'
            >
              <Controller
                name='email'
                control={form.control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    label='Email address'
                    disabled
                    placeholder='Enter your email'
                    dirty={form.formState.dirtyFields.email}
                    error={form.formState.errors.email?.message}
                    style={{ flex: 1 }}
                  />
                )}
              />
              <Flex>
                <Button
                  variant='secondary'
                  onClick={() => setEmailChangeStep('request')}
                  styles={{ alignSelf: 'center', justifyContent: 'center' }}
                >
                  Change email
                </Button>
              </Flex>
            </Flex>
            <Controller
              name='location'
              control={form.control}
              render={({ field }) => (
                <FormInput
                  {...field}
                  disabled
                  label='Location'
                  placeholder='Enter your location'
                  dirty={form.formState.dirtyFields.location}
                  error={form.formState.errors.location?.message}
                />
              )}
            />
            <Flex className='form-group'>
              <Controller
                name='company'
                control={form.control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    label='Company'
                    placeholder='Enter your company'
                    dirty={form.formState.dirtyFields.company}
                    error={form.formState.errors.company?.message}
                    disabled
                  />
                )}
              />
              <Controller
                name='role'
                control={form.control}
                render={({ field }) => (
                  <FormSelect
                    {...field}
                    values={rolesAsOptions.filter(
                      option => option.value === field.value,
                    )}
                    onChange={([v]) => v && field.onChange(v.value)}
                    label='Role'
                    placeholder='Select a role'
                    options={rolesAsOptions}
                    optionLabel={item => item.text}
                    optionValue={item => item.value}
                    dirty={form.formState.dirtyFields.role}
                    error={form.formState.errors.role?.message}
                    disabled
                  />
                )}
              />
            </Flex>
            {/* <Flex className='form-controls'>
            <Button
              variant='accent'
              submit
              disabled={!form.formState.isDirty}
            >
              Save Changes
            </Button>
          </Flex> */}
          </Form>
        </Flex>
      </Flex>

      <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

      <Flex wfull inline={!isTablet} gap={4} wrap>
        <Flex fraction={4} gap={2} wfull>
          <Text variant='h3'>Authentication</Text>
          <Text variant='h4' color='black-light-40'>
            Manage your login methods and security settings. Multi-Factor
            Authentication is required for all accounts.
          </Text>
        </Flex>
        <Flex fraction={6} wfull>
          <AuthenticationDetails />
        </Flex>
      </Flex>

      <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

      <Flex wfull inline={!isTablet} gap={4} wrap>
        <Flex fraction={4} gap={2} wfull>
          <Text variant='h3'>Appearance</Text>
          <Text variant='h4' color='black-light-40'>
            Customize the look and feel of your application.
          </Text>
        </Flex>
        <Flex fraction={6} wfull>
          <Flex wfull inline gap={2} align='center' justify='space-between'>
            <Flex wfull>
              <Text variant='h4' color='black'>
                Theme
              </Text>
              <Text variant='textblock' color='black-light-40'>
                Toggle between Light and Dark mode.
              </Text>
            </Flex>
            <FormToggle
              name='theme-switcher'
              value={theme === 'dark'}
              onChange={toggle}
              isThemeSwitcher
            />
          </Flex>
        </Flex>
      </Flex>

      <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

      <Flex wfull inline align='center' gap={4} wrap>
        <Flex fraction={4} gap={2} wfull>
          <Text variant='h3'>Other actions</Text>
        </Flex>
        <Flex fraction={6} wfull align='end' hfull>
          <Button
            variant='danger'
            styles={{ border: 0, whiteSpace: 'nowrap' }}
            onClick={() => setShowDeleteModal(true)}
          >
            Delete My Account
          </Button>
          <Modal
            title='Change email address'
            isOpen={emailChangeStep === 'request'}
            onCancel={closeEmailChangeModal}
            onSave={handleRequestEmailChange}
            saveLabel={
              isEmailChangeLoading ? 'Sending...' : 'Verify'
            }
            disabled={!pendingEmail || isEmailChangeLoading}
            style={{ width: 480 }}
            preventClickOutsideClose
          >
            <form
              onSubmit={e => { e.preventDefault(); if (pendingEmail && !isEmailChangeLoading) handleRequestEmailChange() }}
              style={{ width: '100%' }}
            >
              <Flex wfull gap={3}>
                <Text color='black-light-40'>
                  Current email: <strong>{user?.email}</strong>
                </Text>
                <FormInput
                  label='New email address'
                  type='email'
                  name='pendingEmail'
                  value={pendingEmail}
                  onChangeText={setPendingEmail}
                  placeholder='Enter your new email address'
                  style={{ width: '100%' }}
                  autoComplete='email'
                />
              </Flex>
            </form>
          </Modal>
          <Modal
            title='Verify new email'
            isOpen={emailChangeStep === 'confirm'}
            onCancel={closeEmailChangeModal}
            onSave={handleConfirmEmailChange}
            saveLabel={isEmailChangeLoading ? 'Verifying...' : 'Confirm change'}
            disabled={!otpCode || isEmailChangeLoading}
            style={{ width: 480 }}
            preventClickOutsideClose
          >
            <form
              onSubmit={e => { e.preventDefault(); if (otpCode && !isEmailChangeLoading) handleConfirmEmailChange() }}
              style={{ width: '100%' }}
            >
              <Flex wfull gap={3}>
                <Text color='black-light-40' style={{ hyphens: 'none' }}>
                  We sent a verification code to <strong>{pendingEmail}</strong>.
                  Enter it below to confirm the change.
                </Text>
                <OtpInput
                  label='Verification code'
                  instructions='Enter the 6-digit code sent to your email'
                  value={otpCode}
                  onChange={code => setOtpCode(code)}
                  autoFocus
                />
              </Flex>
            </form>
          </Modal>
          <Modal
            isOpen={showDeleteModal}
            title='Delete Account'
            onSave={handleDeleteAccount}
            onCancel={() => setShowDeleteModal(false)}
            cancelLabel='No'
            saveLabel='Yes'
          >
            <Flex gap={2}>
              <Text variant='h4' color='black' style={{ hyphens: 'none' }}>
                Are you sure you want to delete your account?
              </Text>
              <Text color='black' style={{ hyphens: 'none' }}>
                This action cannot be undone and all your data will be
                permanently removed, including any associations with
                organizations and other related information.
              </Text>
            </Flex>
          </Modal>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default SettingsBasicInfo
